import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Printer, RefreshCw, Receipt, FileText, CheckCircle2, AlertTriangle, Download, Play } from "lucide-react";
import { listPrinters, launchQzTray, openQzDownload, onPrinterStatusChange, getLastPrinterError, PRINT_STATE, isQzSigned } from "../../helper-components/tsc-label-printer";
import { getPrintSettings, savePrintSettings, receiptProfile, ROLL_PRESETS, DPI_OPTIONS, DEFAULT_RECEIPT_PROFILE } from "../../helper-components/printing/print-settings";
import { printDocument, printLineTest } from "../../helper-components/printing/print-service";
import { LINE_TEST_OPTIONS, lastReceiptPng } from "../../helper-components/printing/escpos";
import { previewHtml } from "../system-settings/invoice-builder/compile";
import { DOC_TYPES } from "../system-settings/invoice-builder/doc-types";
import successToast from "../../helper-components/success-toast";

const isDesktop = () => typeof window.electron?.printHtmlJob === "function" || !!window.electron?.ipcRenderer;

// A test page: a millimetre ruler across the paper shows how much the printer really prints.
function rulerTemplate(widthMm, receipt, about = "") {
  const margins = receipt ? { top: 2, right: 0, bottom: 4, left: 0 } : { top: 10, right: 10, bottom: 10, left: 10 };
  const content = widthMm - margins.left - margins.right;
  const els = [
    { id: "t1", type: "text", x: 1, y: 0, w: Math.min(content, 46), h: 6, content: "Printer test", style: { bold: true, fontSize: 11 } },
    { id: "t2", type: "text", x: 1, y: 6, w: Math.min(content, 46) - 1, h: 10, content: `${about || `Paper ${widthMm} mm`}\nLast number fully printed = printable mm`, style: { fontSize: 6.5, lineHeight: 1.15 } },
    { id: "ln", type: "line", orientation: "h", x: 0, y: 17, w: content, h: 2, style: { lineWidth: 0.5, color: "#000" } },
  ];
  for (let x = 0; x <= content + 0.01; x += 5) {
    const major = x % 10 === 0;
    els.push({ id: "r" + x, type: "line", orientation: "v", x: x - 1, y: 18, w: 2, h: major ? 5 : 3, style: { lineWidth: 0.4, color: "#000" } });
    if (major && x > 0) els.push({ id: "n" + x, type: "text", x: x - 4, y: 23.5, w: 8, h: 4, content: String(x), style: { fontSize: 6, align: "center" } });
  }
  els.push({ id: "box", type: "rect", x: 0, y: 30, w: content, h: 12, style: { borderWidth: 0.75, borderColor: "#000" } });
  els.push({ id: "t3", type: "text", x: 1, y: 31, w: Math.min(content, 46) - 2, h: 10, content: "The box ends where the printer stops.\n{{ NOW() }}", style: { fontSize: 7, valign: "middle" } });
  return {
    version: 1,
    docType: "billing_invoice",
    name: "Printer test",
    paper: receipt
      ? { preset: "CUSTOM", width: widthMm, height: 0, continuous: true, orientation: "portrait", margins }
      : { preset: "A4", width: 210, height: 297, continuous: false, orientation: "portrait", margins },
    defaults: { fontFamily: "Arial", fontSize: 8, color: "#000000" },
    variables: [],
    bands: [{ id: "b", type: "fixed", name: "Test", height: 44, elements: els }],
  };
}

function Card({ title, icon, children, right }) {
  return (
    <div className="bg-white border border-EAECF0 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-000000">
          {icon}
          {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Label({ children, hint }) {
  return (
    <div className="mb-1">
      <div className="text-xs font-medium text-344054">{children}</div>
      {hint && <div className="text-[11px] text-667085">{hint}</div>}
    </div>
  );
}

const selectCls = "w-full border border-EAECF0 rounded-md px-2 py-2 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-focus-color";
const btn = "inline-flex items-center gap-1.5 border border-EAECF0 bg-white text-344054 hover:bg-f3f8ff text-xs font-medium px-3 py-2 rounded-md disabled:opacity-50";

// Number box that keeps what is being typed (empty, "8", "80"...) and only
// saves a valid number; on leaving the box an invalid entry goes back to the value.
function NumberField({ value, onChange, min, max, step = 1, disabled, className, placeholder, allowEmpty }) {
  const [text, setText] = useState(String(value ?? ""));
  useEffect(() => setText(String(value ?? "")), [value]);
  const valid = (t) => {
    const n = parseFloat(t);
    return t.trim() !== "" && !isNaN(n) && (min === undefined || n >= min) && (max === undefined || n <= max);
  };
  return (
    <input
      type="number"
      className={className}
      value={text}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        setText(e.target.value);
        if (valid(e.target.value)) onChange(parseFloat(e.target.value));
        else if (allowEmpty && e.target.value.trim() === "") onChange("");
      }}
      onBlur={() => !valid(text) && setText(String(value ?? ""))}
    />
  );
}

// Thermal printers: the default receipt printer, plus a profile for every
// thermal printer so any model (58 / 76 / 80 / 112 mm, 180-300 dpi...) prints right.
function ThermalPrinters({ settings, set, printers, printerOptions, testPrint }) {
  const [editing, setEditing] = useState(settings.receiptPrinter || "");
  const [lastNumber, setLastNumber] = useState("");
  const [customRoll, setCustomRoll] = useState(false);
  const profile = receiptProfile(settings, editing);
  const matchedPreset = ROLL_PRESETS.find((p) => p.rollWidth === Number(profile.rollWidth) && p.printableWidth === Number(profile.printableWidth));
  const preset = customRoll || !matchedPreset ? "custom" : matchedPreset.id;
  useEffect(() => setCustomRoll(false), [editing]);
  const configured = Object.keys(settings.profiles || {}).filter(Boolean);
  const editable = Array.from(new Set([...(editing ? [editing] : []), ...printers, ...configured]));

  const setProfile = (patch) => set({ profiles: { ...(settings.profiles || {}), [editing]: { ...profile, ...patch } } });

  return (
    <Card title="Thermal / receipt printers" icon={<Receipt size={16} />}>
      <Label hint="Used for formats designed on a thermal roll.">Default receipt printer</Label>
      <select
        className={selectCls}
        value={settings.receiptPrinter}
        onChange={(e) => {
          set({ receiptPrinter: e.target.value });
          setEditing(e.target.value);
        }}
      >
        {printerOptions(settings.receiptPrinter)}
      </select>

      <div className="mt-4 pt-4 border-t border-EAECF0">
        <Label hint="Each thermal printer keeps its own settings.">Settings for printer</Label>
        <select className={selectCls} value={editing} onChange={(e) => setEditing(e.target.value)}>
          <option value="">— Computer's default printer —</option>
          {editable.map((p) => (
            <option key={p} value={p}>
              {p}
              {configured.includes(p) ? " ✓" : ""}
            </option>
          ))}
        </select>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Paper roll</Label>
            <select
              className={selectCls}
              value={preset}
              onChange={(e) => {
                const r = ROLL_PRESETS.find((x) => x.id === e.target.value);
                setCustomRoll(!r);
                if (r) setProfile({ rollWidth: r.rollWidth, printableWidth: r.printableWidth });
              }}
            >
              {ROLL_PRESETS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
              <option value="custom">Custom width…</option>
            </select>
          </div>
          <div>
            <Label>Roll width (mm)</Label>
            <NumberField
              className={selectCls}
              value={profile.rollWidth}
              min={20}
              max={300}
              onChange={(v) => {
                setCustomRoll(true);
                setProfile({ rollWidth: v, printableWidth: Math.min(Number(profile.printableWidth), v) });
              }}
            />
          </div>
          <div>
            <Label hint="The head can't print to the paper edge">Printable width (mm)</Label>
            <NumberField
              className={selectCls}
              value={profile.printableWidth}
              min={10}
              max={Number(profile.rollWidth)}
              step={0.5}
              onChange={(v) => {
                setCustomRoll(true);
                setProfile({ printableWidth: v });
              }}
            />
          </div>
          <div>
            <Label>Resolution</Label>
            <select className={selectCls} value={profile.dpi} onChange={(e) => setProfile({ dpi: Number(e.target.value) })}>
              {DPI_OPTIONS.map((d) => (
                <option key={d.dpi} value={d.dpi}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label hint="Shift if the print isn't centred">Left / right shift (mm)</Label>
            <NumberField className={selectCls} value={profile.offsetMm} min={-20} max={20} step={0.5} onChange={(v) => setProfile({ offsetMm: v })} />
          </div>
          <div>
            <Label hint="Higher prints faint text darker">Darkness ({profile.darkness})</Label>
            <input type="range" className="w-full accent-primary" min={80} max={230} step={5} value={profile.darkness} onChange={(e) => setProfile({ darkness: Number(e.target.value) })} />
          </div>
          <div>
            <Label hint="Direct works with any driver">How receipts are sent</Label>
            <select className={selectCls} value={profile.mode} onChange={(e) => setProfile({ mode: e.target.value })}>
              <option value="escpos">Direct (ESC/POS) — recommended</option>
              <option value="driver">Through the printer driver</option>
            </select>
          </div>
          <div>
            <Label hint="Change only if it prints symbols">Image command</Label>
            <select className={selectCls} value={profile.imageMode} disabled={profile.mode === "driver"} onChange={(e) => setProfile({ imageMode: e.target.value })}>
              <option value="column">Standard (ESC *) — most printers</option>
              <option value="raster">Fast (GS v 0)</option>
            </select>
          </div>
          <div>
            <Label>Cut after each receipt</Label>
            <select className={selectCls} value={profile.cut} disabled={profile.mode === "driver"} onChange={(e) => setProfile({ cut: e.target.value })}>
              <option value="partial">Partial cut</option>
              <option value="full">Full cut</option>
              <option value="none">No cut (tear off)</option>
            </select>
          </div>
          <div>
            <Label hint="Extra paper after the space below (~3 mm each)">Feed lines</Label>
            <NumberField className={selectCls} value={profile.feedLines} min={0} max={10} disabled={profile.mode === "driver"} onChange={(v) => setProfile({ feedLines: Math.round(v) })} />
          </div>
          <div>
            <Label hint="White lines across the text? Try 22, then 21">Strip spacing (dots)</Label>
            <NumberField className={selectCls} value={profile.stripSpacing} min={16} max={30} disabled={profile.mode === "driver" || profile.imageMode === "raster"} onChange={(v) => setProfile({ stripSpacing: Math.round(v) })} />
          </div>
          <div>
            <Label hint="Format wider than the printable width">Wider formats</Label>
            <select className={selectCls} value={profile.fit} disabled={profile.mode === "driver"} onChange={(e) => setProfile({ fit: e.target.value })}>
              <option value="shrink">Shrink to fit (smaller text)</option>
              <option value="actual">Keep exact font size (edges cut)</option>
            </select>
          </div>
          <div>
            <Label hint="Empty = no extra space">Space above (mm)</Label>
            <NumberField className={selectCls} value={profile.topSpaceMm} min={0} max={50} step={0.5} allowEmpty placeholder={profile.trimBlank === false ? "Format's margin" : "0"} disabled={profile.mode === "driver"} onChange={(v) => setProfile({ topSpaceMm: v })} />
          </div>
          <div>
            <Label hint="Before the feed / cut">Space below (mm)</Label>
            <NumberField className={selectCls} value={profile.bottomSpaceMm} min={0} max={50} step={0.5} allowEmpty placeholder={profile.trimBlank === false ? "Format's margin" : "0"} disabled={profile.mode === "driver"} onChange={(v) => setProfile({ bottomSpaceMm: v })} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-344054 mt-3">
          <input type="checkbox" className="accent-primary" checked={profile.trimBlank !== false} disabled={profile.mode === "driver"} onChange={(e) => setProfile({ trimBlank: e.target.checked })} />
          Remove blank space above and below the receipt
        </label>
        <label className="flex items-center gap-2 text-xs text-344054 mt-3">
          <input type="checkbox" className="accent-primary" checked={!!profile.openDrawer} disabled={profile.mode === "driver"} onChange={(e) => setProfile({ openDrawer: e.target.checked })} />
          Open the cash drawer after printing
        </label>

        <div className="mt-4 p-3 rounded-lg bg-f3f8ff text-[11px] text-344054">
          <div className="font-semibold text-000000 mb-1">Calibrate</div>
          1. Print the ruler. 2. Enter the last number you can fully read. The printable width is set from it.
          <div className="flex items-center gap-2 mt-2">
            <button type="button" className={btn} onClick={() => testPrint(true, editing)}>
              <Printer size={13} /> Test print (ruler)
            </button>
            <input type="number" className={selectCls + " !w-24"} placeholder="e.g. 70" value={lastNumber} onChange={(e) => setLastNumber(e.target.value)} />
            <button
              type="button"
              className={btn}
              disabled={!(Number(lastNumber) > 10)}
              onClick={() => {
                // the ruler is numbered every 10 mm with ticks every 5 mm: the head prints a little past the last full number
                setProfile({ printableWidth: Math.min(Number(profile.rollWidth), Number(lastNumber)) });
                setLastNumber("");
                successToast(`Printable width set to ${Math.min(Number(profile.rollWidth), Number(lastNumber))} mm`);
              }}
            >
              Apply
            </button>
          </div>
        </div>
        <div className="mt-3 p-3 rounded-lg bg-f3f8ff text-[11px] text-344054">
          <div className="font-semibold text-000000 mb-1">White lines, cut letters or lines touching the text?</div>
          1. Print the line test: it prints samples A–{LINE_TEST_OPTIONS[LINE_TEST_OPTIONS.length - 1].id}, each with a black bar and a text line with a rule under it. 2. Pick the first one
          whose bar is solid black and whose rule doesn't touch the text.
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button type="button" className={btn} disabled={profile.mode === "driver"} onClick={() => printLineTest(editing)}>
              <Printer size={13} /> Line test
            </button>
            <button
              type="button"
              className={btn}
              title="The exact image sent with the last receipt (since the page was opened)"
              onClick={() => {
                const png = lastReceiptPng();
                if (!png) return successToast("Print a receipt first (without reloading the page), then save it here");
                const a = document.createElement("a");
                a.href = png;
                a.download = "last-receipt.png";
                a.click();
              }}
            >
              <Download size={13} /> Save last receipt image
            </button>
            {LINE_TEST_OPTIONS.map((o) => {
              const active = profile.imageMode === o.imageMode && (o.imageMode === "raster" || Number(profile.stripSpacing) === o.stripSpacing);
              return (
                <button
                  key={o.id}
                  type="button"
                  title={o.label}
                  className={btn + (active ? " !border-primary !text-primary" : "")}
                  disabled={profile.mode === "driver"}
                  onClick={() => {
                    setProfile(o.imageMode === "raster" ? { imageMode: "raster" } : { imageMode: "column", stripSpacing: o.stripSpacing });
                    successToast(`Using ${o.id}: ${o.label}`);
                  }}
                >
                  {o.id}
                </button>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          className="text-[11px] text-667085 hover:text-F51D00 mt-3"
          onClick={() => {
            const profiles = { ...(settings.profiles || {}) };
            delete profiles[editing];
            set({ profiles });
          }}
        >
          Reset this printer to defaults ({DEFAULT_RECEIPT_PROFILE.rollWidth} mm roll)
        </button>
      </div>
    </Card>
  );
}

export default function PrinterSetup() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(getPrintSettings);
  const [state, setState] = useState(PRINT_STATE.CONNECTING);
  const [printers, setPrinters] = useState([]);
  const [busy, setBusy] = useState(false);
  const desktop = isDesktop();

  const refresh = useCallback(async (force) => {
    setState(PRINT_STATE.CONNECTING);
    if (typeof window.electron?.listPrinters === "function") {
      // desktop app: ask it directly, no QZ Tray involved
      let list = [];
      try {
        list = (await window.electron.listPrinters()) || [];
      } catch {}
      setState(PRINT_STATE.OPEN);
      setPrinters(list);
      return;
    }
    const res = await listPrinters({ force });
    setState(res.state);
    setPrinters(res.printers || []);
  }, []);

  useEffect(() => {
    refresh(false);
    return onPrinterStatusChange(() => refresh(true));
  }, [refresh]);

  const set = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    savePrintSettings(next);
  };

  const testPrint = async (receipt, printerName) => {
    const printer = receipt ? printerName : settings.documentPrinter;
    if (!receipt) {
      await printDocument({ html: previewHtml(rulerTemplate(210, false), {}), copies: 1, preview: true }, { printer, docType: null, isTest: true });
      return;
    }
    const p = receiptProfile(settings, printer);
    const roll = Number(p.rollWidth) || 80;
    const about = `${printer || "Default printer"}\nRoll ${roll} mm · printable ${p.printableWidth} mm · ${p.dpi} dpi`;
    // the ruler goes out at true size across the whole roll, so the printer
    // itself shows how far its head reaches
    await printDocument(
      { html: previewHtml(rulerTemplate(roll, true, about), {}), copies: 1, preview: true },
      { printer, docType: null, isTest: true, profileOverride: { printableWidth: roll } }
    );
  };

  const connected = state === PRINT_STATE.OPEN;
  const printerOptions = (value) => {
    const list = printers.includes(value) || !value ? printers : [value, ...printers];
    return [
      <option key="" value="">
        — Computer's default printer —
      </option>,
      ...list.map((p) => (
        <option key={p} value={p}>
          {p}
          {!printers.includes(p) ? " (not found)" : ""}
        </option>
      )),
    ];
  };

  return (
    <div className="h-full overflow-auto" style={{ background: "#f8fafc" }}>
      <div className="px-6 pt-3 pb-4 bg-white border-b border-EAECF0">
        <button className="flex items-center gap-1 text-[11px] text-667085 hover:text-primary" onClick={() => navigate("/settings")}>
          <ChevronLeftIcon className="h-3.5 w-3.5" />
          System Settings
        </button>
        <div className="flex items-center gap-3 mt-1">
          <div className="h-10 w-10 rounded-lg bg-primary text-white flex items-center justify-center">
            <Printer size={20} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-000000 leading-tight">Printer Setup</h1>
            <p className="text-xs text-667085">Print straight to the right printer with the exact paper size, orientation and copies — no print dialog. Saved for this computer.</p>
          </div>
        </div>
      </div>

      <div className="p-6 grid gap-5 max-w-5xl" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))" }}>
        <Card
          title="Printer connection"
          icon={<Printer size={16} />}
          right={
            <button type="button" className={btn} onClick={() => refresh(true)} disabled={state === PRINT_STATE.CONNECTING}>
              <RefreshCw size={13} /> Refresh
            </button>
          }
        >
          {desktop ? (
            <div className="flex items-start gap-2 text-xs text-344054">
              <CheckCircle2 size={16} className="text-027A48 shrink-0" />
              <div>
                <div className="font-semibold text-000000">Desktop app</div>
                The desktop app prints directly — QZ Tray is not needed. {printers.length} printer{printers.length === 1 ? "" : "s"} found.
              </div>
            </div>
          ) : connected ? (
            <div className="flex items-start gap-2 text-xs text-344054">
              <CheckCircle2 size={16} className="text-027A48 shrink-0" />
              <div>
                <div className="font-semibold text-000000">QZ Tray connected</div>
                {printers.length} printer{printers.length === 1 ? "" : "s"} found on this computer.
                {isQzSigned() ? (
                  <div className="text-027A48 mt-1">Requests are signed — tick "Remember this decision" once and QZ Tray stops asking.</div>
                ) : (
                  <div className="text-F51D00 mt-1">
                    Requests are not signed, so QZ Tray asks "Allow" for every print. Copy the QZ certificate and key (qz-keys folder) to this server.
                  </div>
                )}
              </div>
            </div>
          ) : state === PRINT_STATE.CONNECTING ? (
            <div className="text-xs text-667085">Connecting to QZ Tray… (if QZ Tray asks, click Allow and tick "Remember this decision")</div>
          ) : (
            <div className="flex flex-col gap-3 text-xs text-344054">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-F51D00 shrink-0" />
                <div>
                  <div className="font-semibold text-000000">
                    {state === PRINT_STATE.INSECURE_PAGE ? "This page can't reach printers" : "QZ Tray is not running"}
                  </div>
                  {state === PRINT_STATE.INSECURE_PAGE
                    ? "Open the app over https (or localhost) so the browser lets it talk to QZ Tray."
                    : "QZ Tray is a free app that lets the browser print directly to your printers. Install it once, then start it."}
                  {getLastPrinterError() && <div className="text-667085 mt-1">{getLastPrinterError()}</div>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btn}
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setState(await launchQzTray());
                    setBusy(false);
                    refresh(true);
                  }}
                >
                  <Play size={13} /> Start QZ Tray
                </button>
                <button type="button" className={btn} onClick={openQzDownload}>
                  <Download size={13} /> Download QZ Tray
                </button>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-344054 mt-4">
            <input type="checkbox" className="accent-primary" checked={settings.browserFallback} onChange={(e) => set({ browserFallback: e.target.checked })} />
            If no printer connection is available, use the browser print dialog
          </label>
          <label className="flex items-center gap-2 text-xs text-344054 mt-2">
            <input type="checkbox" className="accent-primary" checked={settings.warnBeforePrint} onChange={(e) => set({ warnBeforePrint: e.target.checked })} />
            Warn me before printing when something doesn't match
          </label>
        </Card>

        <Card title="Document printer (A4, A5, Letter…)" icon={<FileText size={16} />}>
          <Label hint="Used for every page format (not thermal rolls).">Printer</Label>
          <select className={selectCls} value={settings.documentPrinter} onChange={(e) => set({ documentPrinter: e.target.value })}>
            {printerOptions(settings.documentPrinter)}
          </select>
          <button type="button" className={btn + " mt-4"} onClick={() => testPrint(false)}>
            <Printer size={13} /> Test print (A4)
          </button>
        </Card>

        <ThermalPrinters settings={settings} set={set} printers={printers} printerOptions={printerOptions} testPrint={testPrint} />

        <Card title="Per document (optional)" icon={<Printer size={16} />}>
          <p className="text-[11px] text-667085 mb-3">Send a document to a specific printer. "Automatic" uses the receipt printer for thermal formats and the document printer for everything else.</p>
          <div className="flex flex-col gap-2 max-h-80 overflow-auto pr-1">
            {DOC_TYPES.map((d) => (
              <div key={d.key} className="grid grid-cols-2 gap-2 items-center">
                <span className="text-xs text-344054">{d.label}</span>
                <select
                  className={selectCls}
                  value={settings.overrides?.[d.key] || ""}
                  onChange={(e) => {
                    const overrides = { ...(settings.overrides || {}) };
                    if (e.target.value) overrides[d.key] = e.target.value;
                    else delete overrides[d.key];
                    set({ overrides });
                  }}
                >
                  <option value="">Automatic</option>
                  {printers.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <button
            type="button"
            className={btn + " mt-3"}
            onClick={() => {
              set({ overrides: {} });
              successToast("Per-document printers reset");
            }}
          >
            Reset all to automatic
          </button>
        </Card>
      </div>
    </div>
  );
}
